import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material";

import {
    handleValidationResponse,
} from "../util/integration";

import {
    VALIDATION,
} from "./../util/configuration";
 
import {
    isUsernameAvailableAPI,
} from "../api/user_api";

import {
    validateNonEmptyAlphanumericString,
} from "../api/validation_api";

/*
 * Structure: useUsernameValidation handles debounced backend validation,
 * useEditUsernameDialog manages dialog state and actions, ValidationAlert
 * renders validation feedback, and EditUsernameDialog defines the UI layout.
 */


const STATUS = Object.freeze({
    idle: "idle",
    checking: "checking",
    valid: "valid",
    invalid: "invalid",
});

const MESSAGES = Object.freeze({
    title:
        "Benutzername ändern",
    usernameLabel:
        "Benutzername",
    closeButton:
        "Schließen",
    saveButton:
        "Speichern",
    required:
        "Bitte einen Benutzernamen eingeben.",
    invalid:
        "Der Benutzername entspricht nicht den Vorgaben.",
    unavailable:
        "Der Benutzername ist bereits vergeben.",
    available:
        "Der Benutzername ist verfügbar.",
    unchanged:
        "Der Benutzername wurde nicht geändert.",
    checkFailed:
        "Die Verfügbarkeit konnte nicht geprüft werden.",
    checking:
        "Verfügbarkeit wird geprüft …",
});

const NOOP = () => {};

const validateWithBackend = (
    validator,
    value,
    showMessage,
) => {
    return new Promise((resolve) => {
        handleValidationResponse(
            validator(value),
            (response) => {
                resolve(Boolean(response));
            },
            () => {
                resolve(false);
            },
            showMessage,
        );
    });
};

function useUsernameValidation({
    accessToken,
    currentUsername,
    enabled,
    showMessage,
    username,
}) {
    const [
        status,
        setStatus,
    ] = useState(STATUS.idle);

    const [
        validationMessage,
        setValidationMessage,
    ] = useState("");

    const validationVersionRef =
        useRef(0);

    useEffect(() => {
        validationVersionRef.current += 1;

        if (!enabled) {
            setStatus(STATUS.idle);
            setValidationMessage("");

            return undefined;
        }

        const validationVersion =
            validationVersionRef.current;

        if (!username) {
            setStatus(STATUS.invalid);
            setValidationMessage(
                MESSAGES.required,
            );

            return undefined;
        }

        if (username === currentUsername) {
            setStatus(STATUS.valid);
            setValidationMessage(
                MESSAGES.unchanged,
            );

            return undefined;
        }

        setStatus(STATUS.checking);
        setValidationMessage(
            MESSAGES.checking,
        );

        const timeoutId = window.setTimeout(
            async () => {
                const syntacticallyValid =
                    await validateWithBackend(
                        validateNonEmptyAlphanumericString,
                        username,
                        showMessage,
                    );

                if (
                    validationVersionRef.current
                    !== validationVersion
                ) {
                    return;
                }

                if (!syntacticallyValid) {
                    setStatus(STATUS.invalid);
                    setValidationMessage(
                        MESSAGES.invalid,
                    );

                    return;
                }

                if (!accessToken) {
                    setStatus(STATUS.invalid);
                    setValidationMessage(
                        MESSAGES.checkFailed,
                    );

                    return;
                }

                const available =
                    await isUsernameAvailableAPI(
                        username,
                        accessToken,
                        showMessage,
                    );

                if (
                    validationVersionRef.current
                    !== validationVersion
                ) {
                    return;
                }

                if (available === null) {
                    setStatus(STATUS.invalid);
                    setValidationMessage(
                        MESSAGES.checkFailed,
                    );

                    return;
                }

                if (!available) {
                    setStatus(STATUS.invalid);
                    setValidationMessage(
                        MESSAGES.unavailable,
                    );

                    return;
                }

                setStatus(STATUS.valid);
                setValidationMessage(
                    MESSAGES.available,
                );
            },
            VALIDATION.validationDelay,
        );

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [
        accessToken,
        currentUsername,
        enabled,
        showMessage,
        username,
    ]);

    return {
        status,
        validationMessage,
    };
}

function useEditUsernameDialog({
    appAPI,
    contextAPI,
    currentUsername,
    onClose,
    onSave,
    open,
}) {
    const [
        value,
        setValue,
    ] = useState("");

    const [
        saving,
        setSaving,
    ] = useState(false);

    const accessToken =
        contextAPI?.accessToken
        ?? contextAPI?.password;

    const showMessage =
        appAPI?.showMessage
        ?? NOOP;

    const normalizedCurrentUsername =
        useMemo(() => {
            return String(
                currentUsername
                ?? "",
            ).trim();
        }, [currentUsername]);

    const normalizedValue =
        value.trim();

    useEffect(() => {
        if (!open) {
            return;
        }

        setValue(normalizedCurrentUsername);
        setSaving(false);
    }, [
        normalizedCurrentUsername,
        open,
    ]);

    const {
        status,
        validationMessage,
    } = useUsernameValidation({
        accessToken,
        currentUsername:
            normalizedCurrentUsername,
        enabled: open,
        showMessage,
        username: normalizedValue,
    });

    const changed =
        normalizedValue
        !== normalizedCurrentUsername;

    const canSave =
        status === STATUS.valid
        && changed
        && !saving;

    const handleValueChange =
        useCallback((event) => {
            setValue(event.target.value);
        }, []);

    const save =
        useCallback(async () => {
            if (!canSave) {
                return;
            }

            setSaving(true);

            try {
                await onSave(normalizedValue);
            } finally {
                setSaving(false);
            }
        }, [
            canSave,
            normalizedValue,
            onSave,
        ]);

    const close =
        useCallback(() => {
            if (!saving) {
                onClose();
            }
        }, [
            onClose,
            saving,
        ]);

    const handleKeyDown =
        useCallback((event) => {
            if (event.key !== "Enter") {
                return;
            }

            event.preventDefault();
            void save();
        }, [save]);

    return {
        canSave,
        close,
        handleKeyDown,
        handleValueChange,
        save,
        saving,
        status,
        validationMessage,
        value,
    };
}

function ValidationAlert({
    status,
    validationMessage,
}) {
    if (status === STATUS.checking) {
        return (
            <Alert
                severity="info"
                icon={
                    <CircularProgress
                        size={18}
                        color="inherit"
                    />
                }
            >
                {validationMessage}
            </Alert>
        );
    }

    if (status === STATUS.valid) {
        return (
            <Alert severity="success">
                {validationMessage}
            </Alert>
        );
    }

    if (status === STATUS.invalid) {
        return (
            <Alert severity="error">
                {validationMessage}
            </Alert>
        );
    }

    return null;
}

export default function EditUsernameDialog({
    appAPI,
    contextAPI,
    dialogTitle = MESSAGES.title,
    currentUsername = "",
    open,
    onClose,
    onSave,
}) {
    const {
        canSave,
        close,
        handleKeyDown,
        handleValueChange,
        save,
        saving,
        status,
        validationMessage,
        value,
    } = useEditUsernameDialog({
        appAPI,
        contextAPI,
        currentUsername,
        onClose,
        onSave,
        open,
    });

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                {dialogTitle}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        autoFocus
                        fullWidth
                        label={MESSAGES.usernameLabel}
                        value={value}
                        disabled={saving}
                        autoComplete="username"
                        error={
                            status
                            === STATUS.invalid
                        }
                        onChange={
                            handleValueChange
                        }
                        onKeyDown={
                            handleKeyDown
                        }
                    />

                    <ValidationAlert
                        status={status}
                        validationMessage={
                            validationMessage
                        }
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={close}
                    disabled={saving}
                >
                    {MESSAGES.closeButton}
                </Button>

                <Button
                    variant="contained"
                    onClick={save}
                    disabled={!canSave}
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            )
                            : undefined
                    }
                >
                    {MESSAGES.saveButton}
                </Button>
            </DialogActions>
        </Dialog>
    );
}